/**
 * ScopeTab — Scope configuration tab content
 * Shows QueryBuilder + TabularPreviewTable + Right Panel for scope configuration
 */

import { AlertTriangle, Loader2, MousePointerClick } from 'lucide-react'
import { forwardRef } from 'react'
import { TabularPreviewTable } from '@/components/tabular/TabularPreviewTable'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import type { TabularColumn, TabularQueryRequest } from '@/services/api/lake'
import { QueryBuilder } from '../QueryBuilder'

type SelectionMode = 'select-header' | 'build-batch'

interface ScopeTabProps {
  // Preview data
  columns: TabularColumn[]
  rows: (string | number | boolean | null)[][]
  totalRows: number
  currentOffset: number
  isLoading: boolean
  isNavigating: boolean
  queryError: string | null

  // Selection state
  readOnly: boolean
  selectionMode: SelectionMode
  headerRowIndex: number | null
  pendingRows: Set<number>
  pendingColumns: Set<number>

  // Handlers
  onQuery: (req: TabularQueryRequest) => void
  onPrevPage: () => void
  onNextPage: () => void
  onRowClick: (rowIdx: number) => void
  onColumnClick: (colIdx: number) => void
  onDragSelect?: (rows: number[], cols: number[]) => void
  onSetSelectionMode: (mode: SelectionMode) => void
  onClearSelection: () => void

  // Right panel content
  renderRightPanel: () => React.ReactNode

  // Column picking mode (for schema builder)
  columnPickingMode?: boolean
  onColumnPicked?: (columnName: string) => void

  // Cell picking mode (for row condition values)
  cellPickingMode?: boolean
  onCellPicked?: (columnName: string, cellValue: string | number | boolean | null) => void
  pickingContext?: import('./types').PickingContext
}

export const ScopeTab = forwardRef<HTMLDivElement, ScopeTabProps>(function ScopeTab(
  {
    columns,
    rows,
    totalRows,
    currentOffset,
    isLoading,
    isNavigating,
    queryError,
    readOnly,
    selectionMode,
    headerRowIndex,
    pendingRows,
    pendingColumns,
    onQuery,
    onPrevPage,
    onNextPage,
    onRowClick,
    onColumnClick,
    onDragSelect,
    onSetSelectionMode,
    onClearSelection,
    renderRightPanel,
    columnPickingMode = false,
    onColumnPicked,
    cellPickingMode = false,
    onCellPicked,
    pickingContext,
  },
  ref
) {
  return (
    <div className="flex-1 flex overflow-hidden max-md:flex-col max-md:overflow-y-auto" ref={ref}>
      {/* Left column: QueryBuilder + TabularPreviewTable + Toolbar */}
      <div className="flex-1 flex flex-col overflow-hidden max-md:h-[70dvh] max-md:flex-none">
        {/* Query builder */}
        <QueryBuilder
          columns={columns}
          onQuery={onQuery}
          totalRows={totalRows}
          filteredRows={rows.length}
          isLoading={isLoading}
        />

        {/* Error */}
        {queryError && (
          <div className="px-3 py-2 text-xs text-destructive flex items-center gap-1 border-b">
            <AlertTriangle className="h-3.5 w-3.5" />
            {queryError}
          </div>
        )}

        {/* Preview table */}
        {isLoading && rows.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <TabularPreviewTable
            columns={columns}
            rows={rows}
            totalRows={totalRows}
            currentOffset={currentOffset}
            onPrevPage={onPrevPage}
            onNextPage={onNextPage}
            isNavigating={isNavigating}
            headerRowIndex={headerRowIndex}
            pendingRows={pendingRows}
            pendingColumns={pendingColumns}
            selectionMode={readOnly ? null : selectionMode}
            onRowClick={onRowClick}
            onColumnClick={onColumnClick}
            onDragSelect={readOnly ? undefined : onDragSelect}
            columnPickingMode={columnPickingMode}
            onColumnPicked={onColumnPicked}
            cellPickingMode={cellPickingMode}
            onCellPicked={onCellPicked}
            pickingContext={pickingContext}
          />
        )}

        {/* Toolbar — hidden in readOnly mode */}
        {!readOnly && (
          <div className="border-t px-3 py-2 flex items-center gap-2 bg-background max-md:flex-wrap">
            <Button
              variant={selectionMode === 'select-header' ? 'default' : 'outline'}
              size="sm"
              className="h-7 text-xs"
              onClick={() =>
                onSetSelectionMode(
                  selectionMode === 'select-header' ? 'build-batch' : 'select-header'
                )
              }
            >
              <MousePointerClick className="h-3 w-3 mr-1" />
              Set header row
            </Button>

            {(pendingRows.size > 0 || pendingColumns.size > 0) && (
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onClearSelection}>
                Clear selection
              </Button>
            )}

            <Badge variant="secondary" className="ml-auto text-xs">
              {selectionMode === 'select-header'
                ? 'Click a row to set as header'
                : pendingRows.size > 0 || pendingColumns.size > 0
                  ? `${pendingRows.size} row${pendingRows.size !== 1 ? 's' : ''} × ${pendingColumns.size} col${pendingColumns.size !== 1 ? 's' : ''} selected`
                  : 'Click or drag to select data'}
            </Badge>
          </div>
        )}

        {/* readOnly status bar */}
        {readOnly && (
          <div className="border-t px-3 py-2 flex items-center gap-2 bg-background">
            <Badge variant="secondary" className="text-xs">
              {pendingRows.size > 0 || pendingColumns.size > 0
                ? `${pendingRows.size} row${pendingRows.size !== 1 ? 's' : ''} × ${pendingColumns.size} col${pendingColumns.size !== 1 ? 's' : ''} selected`
                : 'No selection'}
            </Badge>
            <span className="text-xs text-muted-foreground ml-1">Read-only view</span>
          </div>
        )}
      </div>

      <Separator orientation="vertical" className="max-md:hidden" />

      {/* Right column */}
      {renderRightPanel()}
    </div>
  )
})
